<template>
    <fetch-item :id="itemId">
        <template slot-scope="{response, loading}">
            <div v-if="loading">Loading...</div>
            <div v-else-if="Object.keys(response).length > 0" class="mainContent">
                <div v-if="showModalAD">
                    <transition name="modal">
                        <div class="modal-mask">
                            <div class="modal-wrapper">
                                <div class="modal-dialog modal-small-viewer">
                                    <div class="modal-content modal-small-viewer">
                                        <div class="modal-header">
                                            <button type="button" class="close" @click="showModalAD=false">
                                                <span aria-hidden="true">&times;</span>
                                            </button>
                                            <h4 class="modal-title">Automated Description</h4>
                                        </div>
                                        <div class="modal-body" style="font-size: 13px;">
                                            The automated description is a best guess of the query gene phenotype based on the proximity of the query gene to the centroid of labelled benchmarked groups in multi-dimensional space. To generate this description, we calculate the <a @click="showModalAD = false; showModalPAD = true;">PAD</a> between the query gene and the midpoint of each of the benchmark datasets, locate the nearest neighbor group (or groups, if very close in value) and apply the description for that group or groups.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>
                <div v-if="showModalPAD">
                    <transition name="modal">
                        <div class="modal-mask">
                            <div class="modal-wrapper">
                                <div class="modal-dialog modal-small-viewer">
                                    <div class="modal-content modal-small-viewer">
                                        <div class="modal-header">
                                            <button type="button" class="close" @click="showModalPAD=false">
                                                <span aria-hidden="true">&times;</span>
                                            </button>
                                            <h4 class="modal-title">Phenotypic Angle of Deviation (PAD)</h4>
                                        </div>
                                        <div class="modal-body" style="font-size: 13px;">
                                            The Phenotypic Angle of Deviation (PAD) compensates for the increase in stochastic variation that is observed as phenotypes become more severe. This metric combines the power of similarity metrics, which captures the angle of spread, and distance metrics, which precisely measure the distance between two compared conditions in multidimensional space. To generate this metric, we normalize the distance between two phenotypes by dividing the Euclidean distance between the considered gene-pair by the sum of the distance to WT for the two vectors. We normalize this on a 0 to 1 scale for simplicity- where values approaching 1 are phenotypically the most similar and values approaching 0 are dissimilar. This transforms the Euclidean Distance measures into PAD values.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>


                <h2 class="card-title">{{geneName = response.locusName}}</h2>
                <p>{{response.description}}</p>
                <h4 class="round">Movie Data and Phenotypic Analysis</h4>

                <div class="row">
                    <div class="col-xs-4" v-show="response.em">
                        <div class="card">
                            <router-link to="/embdProjectDescription">
                            <h5 class="card-title text-center">Embryonic Development<br/>
                                Green, Khaliullin et al., 2024</h5>
                            </router-link>
                            <img class="card-img-top" src="~@/assets/embd-card.png" alt="Embryonic Development Title Image">
                              <div class="card-body">
                                <p class="card-text medium"><label>Automated Description <a @click="showModalAD = true;">(explain)</a>:</label></p>
                                <div class="card-row" v-for="(value, key) in response.em_data" v-bind:key="key">
                                    <label style="font-style: oblique; padding-right: 4px">{{geneName + '(RNAi)'}}</label>- EMBD{{zeroPad(value.embd_id, 4)}}
                                  <p class="medium">{{value.auto_description}}</p>
                                  <a :href="'/embdView/' + value.embd_id" class="btn btn-primary">Link to Data</a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-xs-4" v-show="response.ee">
                        <div class="card">
                            <router-link to="/eecdProjectDescription">
                            <h5 class="card-title text-center">Early Embryonic Cell Division<br/>
                                Sonnichsen et al., 2005</h5>
                            </router-link>
                            <img class="card-img-top" src="~@/assets/eecd-card.png" alt="Early Embryonic Cell Division Title Image">
                            <div class="card-body">
                                <div class="card-row" v-for="(value, key) in collapse(response.ee_data)" v-bind:key="key">
                                    <p class="card-text medium"><label>{{decodeAssayType(key)}}:</label></p>
                                    <p class="medium">{{unique(value).join(', ')}}</p>
                                </div>
                              <p class="card-text medium"><label>Probes:</label></p>
                                {{lodash.uniq(lodash.map(response.ee_data, d => d.probe), lodash.isEqual).join(', ')}}
                            </div>
                            <div class="card-footer">
                                <a :href="'/eecdView/' + response.locusName" class="btn-lg btn-primary">Link to Data</a>
                            </div>
                        </div>
                    </div>

                    <div class="col-xs-4" v-show="response.gm">
                        <div class="card">
                            <router-link to="/gmProjectDescription">
                            <h5 class="card-title text-center">Germline Morphology<br/>
                                Green et al., 2011
                            </h5>
                            </router-link>
                            <img class="card-img-top" src="~@/assets/gm-card.png" alt="Germline Morphology Title Image">
                            <div class="card-body">
                                <div v-for="(value, index) in response.gm_data" v-bind:key="index">
                                    <div class="card-row">
                                        <p class="card-text medium"><label>Broad Category:</label></p>
                                        <p class="medium">{{value.broadCategory}}</p>
                                    </div>
                                    <div class="card-row">
                                        <p class="card-text medium"><label>Phenotypic Class:</label></p>
                                        <p class="medium">{{value.class}}</p>
                                    </div>
                                </div>
                            </div>
                            <div class="card-footer">
                                <a :href="'/gmView/' + response.locusName" class="btn-lg btn-primary">Link to Data</a>
                            </div>
                        </div>
                    </div>
                </div>

                <h4 class="round" data-toggle="collapse" data-target="#sub-contents" aria-expanded="true" aria-controls="sub-contents">
                    <i class="glyphicon glyphicon-play" aria-hidden="true"></i>Additional Content</h4>

                <div id="sub-contents" class="collapse show">
                    <!-- Gene Ontology (GO) Ribbon -->
                    <h4 class="round subcontent collapsed" data-toggle="collapse" data-target="#ribbon" aria-expanded="false" aria-controls="ribbon">
                        <i class="glyphicon glyphicon-play" aria-hidden="true"></i>Gene Ontology (GO) Ribbon <sub>[GeneOntology/REST-API]</sub></h4>

                    <div id="ribbon" class="ribbon collapse">
                        <ribbon-component :search="'WB:' + response.wbgene_id">
                        </ribbon-component>
                    </div>
                    <!-- end of Gene Ontology (GO) Ribbon -->

                    <!-- Interacting Genes -->
                    <h4 class="round subcontent collapsed" data-toggle="collapse" data-target="#interacting-genes" aria-expanded="false" aria-controls="interacting-genes">
                        <i class="glyphicon glyphicon-play" aria-hidden="true"></i>Interacting Genes
                        <sub>[Source: WormBase-WS276]</sub> <span class="badge badge-pill badge-primary" v-if="response.interacting_genes !== null && response.interacting_genes.split(',').length > 0">{{response.interacting_genes.split(',').length}}</span></h4>

                    <div id="interacting-genes" class="collapse" v-bind:class="{show: response.interacting_genes !== null && !interactingGeneReady}">
                        <div class="card">
                            <div class="card-body">
                                <p class="card-text">{{response.interacting_genes}}</p>
                            </div>
                            <cyto v-if="response.interacting_genes !== null" :master_gene="response.locusName" :interacting_genes="response.interacting_genes" :show.sync="interactingGeneReady"></cyto>
                        </div>
                    </div>
                    <!-- end of Interacting Genes -->

                    <!-- Human Ortholog -->
                    <h4 class="round subcontent collapsed" data-toggle="collapse" data-target="#human-ortholog" aria-expanded="false" aria-controls="human-ortholog">
                        <i class="glyphicon glyphicon-play" aria-hidden="true"></i>Human Ortholog
                        <sub>[Source: Ortholist2]</sub> <span class="badge badge-pill badge-primary" v-if="response.h_gene_name !== null && response.h_gene_name.split(',').length > 0">{{response.h_gene_name.split(',').length}}</span></h4>

                    <div id="human-ortholog" class="collapse">
                        <div class="card">
                            <div class="card-body">
                                <div class="card-text" v-if="response.h_gene_name !== null" v-html="renderOrtholog2(response.h_gene_name.split(','))">
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- end of Human Ortholog -->

                    <!-- Disease Relevance -->
                    <h4 class="round subcontent collapsed" data-toggle="collapse" data-target="#disease-relevance" aria-expanded="false" aria-controls="disease-relevance">
                        <i class="glyphicon glyphicon-play" aria-hidden="true"></i>Human Diseases
                        <sub>[Wormbase/REST-API]</sub> <span class="badge badge-pill badge-primary" v-show="noHumanDiseases > 0">{{noHumanDiseases}}</span></h4>

                    <div id="disease-relevance" class="collapse">
                        <div class="card">
                            <div class="card-body">
                                <p class="card-text">
                                    <fetch-human-diseases :wb-gene-id="response.wbgene_id">
                                        <template slot-scope="{response, loading}">
                                            <div v-if="loading">Loading...</div>
                                            <div v-else-if="Object.keys(response).length > 0 && checkHumanDiseases(response.fields.human_diseases.data)" class="container-fluid">
                                                <ol>
                                                    <li v-for="(model, index) in response.fields.human_diseases.data.potential_model" v-bind:key="'disease-' + index">
                                                        {{geneName}} is a potential model for <a :href="'https://wormbase.org/resources/disease/' + model.id" target="_blank">{{model.label}} ({{model.id}})</a>
                                                    </li>
                                                </ol>
                                            </div>
                                        </template>
                                    </fetch-human-diseases>
                                </p>
                            </div>
                        </div>
                    </div>
                    <!-- end of Disease Relevance -->

                    <!-- All Observed Phenotypes (Wormbase link) -->
                    <h4 class="round subcontent collapsed" data-toggle="collapse" data-target="#observed-phenotypes" aria-expanded="false" aria-controls="observed-phenotypes">
                        <i class="glyphicon glyphicon-play" aria-hidden="true"></i>All Observed Phenotypes
                        <sub>[Wormbase/REST-API]</sub> <span class="badge badge-pill badge-primary" v-show="noPhenotypes > 0">{{noPhenotypes}}</span></h4>

                    <div id="observed-phenotypes" class="collapse">
                        <div class="card">
                            <div class="card-body">
                                <p class="card-text">
                                    <fetch-phenotypes :wb-gene-id="response.wbgene_id">
                                        <template slot-scope="{response, loading}">
                                            <div v-if="loading">Loading...</div>
                                            <div v-else-if="Object.keys(response).length > 0" class="container-fluid">
                                                <ol>
                                                    <li v-for="(model, index) in mapFilter(response.fields.phenotype.data, 'phenotype', { 'taxonomy': 'all' })" v-bind:key="'pheno-' + index">
                                                        <a :href="'https://wormbase.org/species/all/phenotype/' + model.id" target="_blank">
                                                        {{model.label}} ({{model.id}})
                                                        </a>
                                                    </li>
                                                </ol>
                                            </div>
                                        </template>
                                    </fetch-phenotypes>
                                </p>
                            </div>
                        </div>
                    </div>
                    <!-- end of All Observed Phenotypes (Wormbase link) -->
                </div>
            </div>
            <div v-else>
                No record
            </div>
        </template>
    </fetch-item>
</template>

<script>
  import FetchItem from './FetchItem.vue'
  import RibbonComponent from '@/components/Ribbon/GORibbon';
  import Cyto from '@/components/Cyto.vue';
  import FetchHumanDiseases from './FetchHumanDiseases'
  import FetchPhenotypes from './FetchPhenotypes'

  const _ = require('lodash');

  export default {
    name: "ItemView.vue",
    components: {
      FetchItem, RibbonComponent, Cyto, FetchHumanDiseases, FetchPhenotypes
    },
    data() {
      return {
        itemId: this.$route.params.id,
        geneName: '',
        interactingGeneReady: false,
        noHumanDiseases: 0,
        noPhenotypes: 0,
        publicPath: process.env.BASE_URL,
        lodash: _,
        showModalAD: false,
        showModalPAD: false,
      }
    },
    methods: {
      zeroPad(num, places) {
        var zero = places - num.toString().length + 1;
        return Array(+(zero > 0 && zero)).join("0") + num;
      },
      decodeAssayType(assayType) {
        switch (assayType) {
          case '2' : return 'Injection';
          case '4' : return 'Feeding';
          case '8' : return 'Early Embryonic';
          case '16' : return 'Survival';
          case '32' : return 'Larval';
          case '128' : return 'Post Embryonic';
          case '256' : return 'GFP Histone';
          case '512' : return 'GFP Tubulin';
          case '1024' : return 'GFP Gama Tubulin';
          case '2048' : return 'GFP Histone and Tubulin';
        }
      },
      collapse(data) {
        return _.transform(_.uniqWith(data, _.isEqual), function(result, item) {
          if(item.className) {
            (result[item.testType] || (result[item.testType] = [])).push(item.className);
          }
        }, {});
      },
      unique(data) {
        return _.uniqWith(data, _.isEqual)
      },

      checkHumanDiseases(data) {
        const vm = this
        if(data !== null && data.potential_model) {
          vm.noHumanDiseases = data.potential_model.length
          return true
        } else {
          return false
        }
      },
      mapFilter(data, item, filter) {
        let retVal = _.uniqBy(_.filter(_.map(data, item), filter), 'id');
        this.noPhenotypes = retVal.length
        return retVal
      },
      renderHumanOrtholog(data) {
        let orthos = [];
        _.each(data, (i) => {
          orthos.push(`<a href="https://wormbase.org/species/h_sapiens/gene/${i.id}" target="_blank">${i.label}</a>`)
        })
        return orthos.join(', ')
      },
      renderOrtholog2(data) {
        let orthos = [];
        _.each(data, (i) => {
          orthos.push(`<a href="https://www.ensembl.org/Homo_sapiens/geneview?gene=${i.trim()}" target="_blank">${i}</a>`)
        })
        return orthos.join(', ')
      }
    },
    mounted() {
      // const vm = this
      // /* eslint-disable no-console */
      // console.log(vm.itemId);
    },
  }
</script>

<style scoped>
    @import url('~@/assets/bootstrap.css');
    @import url('~@/assets/datatable.css');

    .card-row {
        margin-bottom: 10px;
    }

    .large {
        font-size: medium;
    }

    .medium {
        margin-bottom: 0;
    }

    .card {
        width:100%;
    }

    .row, .row > div[class*='col-'] {
        display: -webkit-box;
        display: -moz-box;
        display: -ms-flexbox;
        display: -webkit-flex;
        display: flex;
        flex:0 0 auto;
    }

    [data-toggle="collapse"] i {
        -webkit-transition-duration: 0.5s;
        -moz-transition-duration: 0.5s;
        -o-transition-duration: 0.5s;
        transition-duration: 0.5s;
        display: inline-block;
        -webkit-transition-property: -webkit-transform;
        -moz-transition-property: -moz-transform;
        -o-transition-property: -o-transform;
        transition-property: transform;

        -webkit-transform:rotate(90deg);
        -moz-transform:rotate(90deg);
        -o-transform:rotate(90deg);
        transform:rotate(90deg);
    }

    [data-toggle="collapse"].collapsed i {
        -webkit-transition-duration: 0.5s;
        -moz-transition-duration: 0.5s;
        -o-transition-duration: 0.5s;
        transition-duration: 0.5s;
        display: inline-block;
        -webkit-transition-property: -webkit-transform;
        -moz-transition-property: -moz-transform;
        -o-transition-property: -o-transform;
        transition-property: transform;

        -webkit-transform:rotate(0deg);
        -moz-transform:rotate(0deg);
        -o-transform:rotate(0deg);
        transform:rotate(0deg);
    }

    .rotate-180 {
        -webkit-transform:rotate(90deg);
        -moz-transform:rotate(90deg);
        -o-transform:rotate(90deg);
    }

    .subcontent {
        margin-left: 20px;
    }

    .round {
        font-family: Calibri, Candara, Segoe, Segoe UI, Optima, Arial;
        border-radius: 7px;
        border: 1px solid #d9d9d9;
        background: #dae3f3;
        padding: 3px 7px 3px 7px;
        font-style: oblique;
        color: #203864;
        font-weight: bold;
    }

    p.strong {
        text-decoration: underline;
        font-style: oblique;
    }

    h2.card-title {
        margin-top: 0;
        color: #2f5597;
        font-style: oblique;
    }

    h5.card-title {
        color: #2f5597;
        font-style: oblique;
        font-weight: bold;
    }

    .mainContent {
        border-radius: 25px;
        border: 2px solid #dae3f3;
        padding: 20px;
    }

    .ribbon {
        -webkit-box-sizing: content-box;
        box-sizing: content-box;
    }
    .ribbon:before,
    .ribbon:after {
        -webkit-box-sizing: content-box;
        box-sizing: content-box;
    }

    .ontology-ribbon__category {
        height: 150px;
    }

    .ontology-ribbon__category__block {
        font-size: 1.2rem;
        height: 150px;
    }

    .ontology-ribbon__item {
        font-size: 1.2rem;
    }
    .ontology-ribbon__block {
        font-size: 1.2rem;
    }
    .ontology-ribbon__header {
        font-size: 1.2rem;
    }
    .ontology-ribbon__assoc__row {
        font-size: 1.3rem;
    }
    .ontology-ribbon__content {
        font-size: 1.2rem;
    }

    .ontology-ribbon__evidence-row {
        font-size: 1.2rem;
    }
    /*.ontology-ribbon__term-column {*/
        /*max-width: 30%;*/
        /*-webkit-box-flex: 30%;*/
        /*-ms-flex: 30%;*/
        /*flex: 30%;*/
    /*}*/

    .ontology-ribbon__assoc {
        width: 80%;
    }

    .modal-title {
        color: #114188;
        font-weight: bold;
        font-size: 18px;
    }

    .modal-mask {
        position: fixed;
        z-index: 9998;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, .5);
        display: table;
        transition: opacity .3s ease;
    }

    .modal-wrapper {
        display: table-cell;
        vertical-align: middle;
    }

    .modal-small-viewer {
        width: 600px;
    }
</style>
